import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener,
  Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, Renderer2, TemplateRef, ViewChild
} from '@angular/core';

import { DOCUMENT, isPlatformServer, NgTemplateOutlet } from '@angular/common';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { map, pairwise } from 'rxjs/operators';
import { Direction } from '../../_enums/direction.enum';
import { TranslateModule } from '@ngx-translate/core';
import { SdPushCardComponent } from '../push-card/push-card.component';
import { IPromo } from '../product-promo/product-promo.interface';
import { SdLanguageSelectorComponent } from '../language-selector/language-selector.component';
import { SdSearchComponent } from '../search/search.component';
import { IDropdownCart } from '../navigation-dropdown/dropdown-cart/dropdown-cart.interface';
import { SdDropdownAccountComponent } from '../navigation-dropdown/dropdown-account/dropdown-account.component';
import { SdDropdownCartComponent } from '../navigation-dropdown/dropdown-cart/dropdown-cart.component';
import { SdDropdownHelpComponent } from '../navigation-dropdown/dropdown-help/dropdown-help.component';
import { SdMenuComponent } from '../menu/menu.component';
import { IPageNavigation } from '../page-navigation/page-navigation.interface';
import { SdPageNavigationComponent } from '../page-navigation/page-navigation.component';
import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { DeviceService } from '../../_services/device.service';
import { fadeInOutAnimation } from '../../_animations/fade-in-out.animation';
import { GetElementUtils } from '../../utils/getElement.utils';
import { IconSizeEnum } from '../../_enums/icon.enum';
import {
  IDropdownAccountData,
  IHeaderIconLink,
  IIconLabel,
  ILanguageSelectorData,
  IMenuDataConfig,
  ISearchData
} from '../../_interfaces/header.interface';
import { ILink } from '../../_interfaces/link.interface';
import { IPushCard, IPushCardWithLayout } from '../../_interfaces/push-card.interface';
import { PositionEnum } from '../../_enums/position.enum';
import { SdLibConfig } from '../../sd-lib-config';
import { SdHeaderReinsurancesComponent } from '../header-reinsurances/header-reinsurances.component';
import { SdButtonComponent } from '../buttons/button/button.component';
import { SdButtonIconComponent } from '../buttons/button-icon/button-icon.component';
import { SdIconComponent } from '../icons/icon/icon.component';
import { SdLinkComponent } from '../links/link/link.component';

/** @dynamic */
@Component({
  selector: 'sd-header',
  templateUrl: './header.component.html',
  animations: [
    fadeInOutAnimation({
      anchor: 'fadeInOut',
      durationIn: 0,
      delayIn: 0,
      durationOut: 0,
      delayOut: 0
    })
  ],
  standalone: true,
  imports: [
    SdLinkComponent,
    SdIconComponent,
    SdLanguageSelectorComponent,
    SdButtonComponent,
    SdButtonIconComponent,
    SdSearchComponent,
    NgTemplateOutlet,
    SdDropdownHelpComponent,
    SdDropdownAccountComponent,
    SdDropdownCartComponent,
    SdMenuComponent,
    SdPageNavigationComponent,
    TranslateModule,
    SdHeaderReinsurancesComponent
  ]
})
export class SdHeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input()
  pushTemplate: TemplateRef<SdPushCardComponent>;

  @Input()
  showPageNavigation: boolean;

  @Input()
  themesConfig: ThemesEnum.light | ThemesEnum.dark | BrandThemesEnum.brand =
    ThemesEnum.light;

  @Input()
  alpha = false;

  @Input()
  isSimplified = false;

  @Input()
  inputMinLength: number;

  @Input()
  deliveryContactData: IIconLabel[];

  @Input()
  dropdownAccountData: IDropdownAccountData;

  @Input()
  dropdownCartData: IDropdownCart;

  @Input()
  dropdownHelpData: ILink[];

  @Input()
  searchData: ISearchData;

  @Input()
  pageNavigationData: IPageNavigation;

  @Input()
  iconLinks: IHeaderIconLink[];

  @Input()
  menuDataConfig: IMenuDataConfig;

  @Input()
  languageSelectorData: ILanguageSelectorData;

  @Input()
  logoLink: ILink;

  @Input()
  isBundle: boolean;

  @Output()
  delete = new EventEmitter<string>();

  @Output()
  languageSelected = new EventEmitter<string>();

  @Output()
  login = new EventEmitter<void>();

  @Output()
  logout = new EventEmitter<void>();

  @Output()
  signUp = new EventEmitter<void>();

  @Output()
  back = new EventEmitter<void>();

  @Output()
  inputSearch = new EventEmitter<string>();

  @Output()
  submitSearch = new EventEmitter<string>();

  @Output()
  clearInputSearch = new EventEmitter<void>();

  @Output()
  cart = new EventEmitter<void>();

  @Output()
  notify = new EventEmitter<void>();

  @Output()
  favorite = new EventEmitter<void>();

  @Output()
  menuOpen = new EventEmitter<boolean>();

  @Output()
  scrolled = new EventEmitter<boolean>();

  @Output()
  clickMenu = new EventEmitter<string>();

  @Output()
  empty = new EventEmitter<void>();

  @Output()
  quantity = new EventEmitter<{ product: string; qty: number }>();

  @Output()
  isAccountOpenedChange = new EventEmitter<boolean>();

  @Output()
  isCartOpenedChange = new EventEmitter<boolean>();

  @Output()
  searchElementClicked = new EventEmitter<string>();

  @Output()
  promoClick = new EventEmitter<IPromo>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @Output()
  pushCardClicked = new EventEmitter<IPushCard | IPushCardWithLayout>();

  @Output()
  clickCardFrames = new EventEmitter<IPushCard | IPushCardWithLayout>();

  @Output()
  linkClicked = new EventEmitter<any>();

  @ViewChild('menuComponent')
  public menuComponent: SdMenuComponent;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.alpha ? 'is-alpha' : '',
      this.menuHidden ? 'is-scrolling-down' : '',
      this.isScrolled ? 'is-scrolled' : '',
      this.showPageNavigation ? 'has-navigation' : '',
      this.isSimplified ? 'is-simplified' : '',
      this.showOverlay ? 'is-open' : '',
      this.themesConfig ? 'is-theme-' + this.themesConfig : 'is-theme-light'
    ]
      .filter(Boolean)
      .join(' ');
  }

  subscriptions = new Subscription();

  isSSR!: boolean;
  textConfig: PositionEnum;
  themesEnum = ThemesEnum;
  brandThemesEnum = BrandThemesEnum;
  getElementUtils = GetElementUtils;
  iconSize = IconSizeEnum;
  isDevice$: boolean;
  isMobile$: boolean;
  menuHidden = false;
  isScrolled = false;
  showOverlay: boolean;
  baseThemesConfig: ThemesEnum.light | ThemesEnum.dark | BrandThemesEnum.brand;
  positionEnum = PositionEnum;
  openMobileSearch: Subject<void> = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) platformId: any,
    private cdRef: ChangeDetectorRef,
    private sdLibConfig: SdLibConfig,
    private elRef: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private deviceService: DeviceService,
  ) {
    this.textConfig = this.sdLibConfig.get().headerLayout;
    this.isSSR = isPlatformServer(platformId);
  }

  /**
   * Close Menu if focus is outside the header component
   */
  @HostListener('focusout', ['$event'])
  onFocusOut(event) {
    const target = event.target || event.currentTarget;
    if (!this.isSimplified && !this.elRef.nativeElement.contains(target)) {
      this.menuComponent?.closeMenu();
    }
  }

  /**
   * Close Menu if focus is outside the header component
   */
  @HostListener('document:keydown.tab', ['$event'])
  onTabsOut(event) {
    const target = event.target || event.currentTarget;
    if (!this.isSimplified && !this.elRef.nativeElement.contains(target)) {
      this.menuComponent?.closeMenu();
    }
  }

  ngOnInit() {
    this.baseThemesConfig = this.themesConfig;

    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );
  }

  ngAfterViewInit() {
    if (!this.isSSR) {
      this.subscriptions.add(
        fromEvent(window, 'scroll').pipe(
          map(() => window.pageYOffset),
          pairwise(),
          map(([y1, y2]): [number, Direction] => [y2, y2 < y1 ? Direction.up : Direction.down])
        ).subscribe(([yOffset, direction]) => {
          if (
            direction === Direction.up ||
            yOffset <= 55 ||
            (direction === Direction.down && yOffset <= 0)
          ) {
            this.menuHidden = false;
            this.isScrolled = false;
            this.menuOpen.emit(!this.menuHidden);
            this.scrolled.emit(!this.isScrolled);
          }

          if (direction === Direction.down && yOffset > 0) {
            this.menuHidden = true;
            this.menuOpen.emit(!this.menuHidden);
          }

          if (direction === Direction.up && yOffset > 0) {
            this.isScrolled = true;
            this.scrolled.emit(!this.isScrolled);
          }

          const body = this.getElementUtils.byId('appWrapper', this.document.body);

          if (body.classList.contains('has-no-overflow')) {
            this.menuHidden = false;
            this.menuOpen.emit(!this.menuHidden);
          }
          this.cdRef.markForCheck();
        })
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  changeLanguage(value: string) {
    this.languageSelected.emit(value);
  }

  onSignUp() {
    this.signUp.emit();
  }

  onLogin() {
    this.login.emit();
  }

  onLogout() {
    this.logout.emit();
  }

  onDelete(product: string) {
    this.delete.emit(product);
  }

  goBack() {
    this.back.emit();
  }

  onOpenedSearch(event?: string) {
    if (event) {
      this.searchElementClicked.emit(event);
    }
    this.menuComponent?.closeMenu();
  }

  onSearch(event: string) {
    this.inputSearch.emit(event);
  }

  onSubmitSearch(event: string) {
    this.submitSearch.emit(event);
  }

  onClearInputSearch() {
    this.clearInputSearch.emit();
  }

  onAddToCart() {
    this.cart.emit();
  }

  onEmptyCart() {
    this.empty.emit();
  }

  onQuantityChange(event) {
    this.quantity.emit({ product: event.product, qty: event.qty });
  }

  onNotify() {
    this.notify.emit();
  }

  onAddToFavorite() {
    this.favorite.emit();
  }

  onClickMenu(event) {
    this.clickMenu.emit(event);
  }

  onMenuOpenedChanged(event: boolean) {
    if (!this.isSSR) {
      this.showOverlay = event;
      const body = this.getElementUtils.byId('appWrapper', this.document.body);
      if (event === true) {
        this.renderer.addClass(body, 'has-no-overflow');
        if (this.isDevice$) {
          this.themesConfig = ThemesEnum.light;
        }
      } else {
        if (body.classList.contains('has-no-overflow')) {
          this.renderer.removeClass(body, 'has-no-overflow');
        }
        if (this.isDevice$) {
          this.themesConfig = this.baseThemesConfig;
        }
      }
    }
  }

  onClickOverlay() {
    this.menuComponent?.closeMenu();
  }

  onClickPromo(promo: IPromo) {
    this.promoClick.emit(promo);
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  onPushCardClicked(event: IPushCard | IPushCardWithLayout) {
    this.pushCardClicked.emit(event);
  }

  onClickCardFrames(event: IPushCard | IPushCardWithLayout) {
    this.clickCardFrames.emit(event);
  }

  onLinkClicked(event) {
    this.linkClicked.emit(event);
  }

  toggleSearch() {
    this.openMobileSearch.next();
  }
}
